import intlTelInput, { type Iti } from 'intl-tel-input';
import type { Iso2 } from 'intl-tel-input/data';

import type { Nullable } from '@src/shared/types/common';

window.intlTelInput = intlTelInput;
/**
 * Глобальное хранилище экземпляров IntlTelInput, связанных с HTMLInputElement
 *
 * @type {WeakMap<HTMLInputElement, Iti>}
 *
 * @description
 * WeakMap для хранения связей между HTML-инпутами и экземплярами IntlTelInput.
 *
 * @example
 * // Получение экземпляра Iti для input элемента
 * const input = document.querySelector('#phone-input');
 * const itiInstance = itiInstances.get(input);
 * @see getIntlTelInputInstance
 * @property {WeakMap} itiInstances - Карта связей input -> Iti instance
 */
const itiInstances: WeakMap<HTMLInputElement, Iti> = new WeakMap();

function NumberOrNull(obj: Nullable<string | number> | undefined): Nullable<number> {
    if (obj) {
        const num = Number(obj);
        return isNaN(num) ? null : num;
    }

    return null;
}

/**
 * возвращает null вместо объекта с id-шником 0
 * */
function simpleDtoOrNull(id: number): Nullable<{ Id: number }> {
    return id && !isNaN(id) ? { Id: id } : null;
}

function getMaxOfArray(numArray: number[]): number {
    return Math.max.apply(null, numArray);
}

function arrayContainsArray(superset: unknown[], subset: unknown[]): boolean {
    if (!subset.length || superset.length < subset.length) {
        return false;
    }

    for (let i = 0; i < subset.length; i++) {
        if (superset.indexOf(subset[i]) === -1) {
            return false;
        }
    }

    return true;
}

function lockElement(lock: boolean, element?: JQuery) {
    const target = element ?? $(document.body);
    const floatingButtons = $('.floatingButtons');
    kendo.ui.progress(target, lock);

    if (!element) {
        target.css({
            overflow: lock ? 'hidden' : ''
        });
    }

    if (floatingButtons.length) {
        floatingButtons.css({
            opacity: lock ? '0.7' : '',
            'pointer-events': lock ? 'none' : ''
        });
    }
}

function audioBlock(id: number, record: string) {
    $('#audioFileDiv').append(`
        <audio id="audio${id}" style="position: relative; top: 11px;" preload="metadata" controls>
            <source src="/Audio/PlayFile?filePath=${record}#" type="audio/mpeg">
        </audio>
        <select id="select${id}" class="speed" onchange="speed_onChange?.(this)">
            <option>1x</option>
            <option>1.25x</option>
            <option>1.5x</option>
            <option>1.75x</option>
            <option>2x</option>
            <option>3x</option>
            <option>4x</option>
        </select>
    `);
}

function PhoneOrContact(obj: string): Nullable<string | number> {
    if (obj) {
        let cleanedContact: string = obj.replace(/[^\wа-яА-Я]/gi, '');
        if (cleanedContact.length === 11 && cleanedContact.startsWith('8')) {
            cleanedContact = cleanedContact.replace(/^8/, '7');
        }
        const phoneRaw: number = Number(cleanedContact);
        return isNaN(phoneRaw) ? obj : phoneRaw;
    }
    return null;
}

function formatPhoneNumber(contact: string): string {
    const cleanPhone = contact.replace(/\D/g, '');
    if (!cleanPhone) {
        return contact;
    }

    const e164 = `+${cleanPhone}`;
    const formatter = intlTelInput.utils;
    return formatter ? (formatter.formatNumber(e164, undefined, formatter.numberFormat.INTERNATIONAL) || e164) : e164;
}

/**
 * функция добавляет иконки к номерам телефонов, игнорируя емейлы
 * */
function addPhoneIcons(contactInfo: string, isCenter: boolean = true, withTelegram: boolean = false): string {
    if (!contactInfo) {
        return '';
    }

    let result: string = '';
    const containerClasses = `contact-cell${isCenter ? '' : ' contact-cell_left'}`;
    const contacts: string[] = contactInfo
        .split(/(<br>|<br\/>|<\/br>|\n)/)
        .map((item) => item.replace(/<\/?br(\/)?>/g, ''))
        .filter((item) => item);

    contacts.forEach((contact, index) => {
        switch (true) {
            case /^\+?\d{10,15}$/.test(contact): {
                const cleanContact = contact.replace(/\D/g, '');
                const formattedPhone = formatPhoneNumber(cleanContact);

                result += `
                    <div class="${containerClasses}">
                        ${formattedPhone}
                        <button class="icon-container" onclick="makeCall('${cleanContact}', this)" type="button">
                            <img class="callLink" src="/img/Phone-info.svg" alt="phone">
                        </button>
                        <a href="https://wa.me/${cleanContact}" target="_blank" rel="noopener noreferrer">
                            <img class="icon_whatsapp" src="/img/WhatsApp.svg" alt="whatsapp">
                        </a>
                        ${withTelegram ? `
                        <a href="https://t.me/+${cleanContact}" target="_blank" rel="noopener noreferrer">
                            <img class="icon_telegram" src="/img/Telegram.svg" alt="telegram">
                        </a>` : ''}
                    </div>
                `;
                break;
            }
            case /^\d{3,}$/.test(contact): {
                result += `
                    <div class="${containerClasses}">
                        ${contact}
                        <a class="icon-container" onclick="makeCall('${contact}', this)">
                            <img class="callLink" src="/img/Phone-info.svg" alt="phone">
                        </a>
                    </div>
                `;
                break;
            }
            case /^https?:\/\//.test(contact): {
                result += `
                    <div class="${containerClasses}">
                        <a href="${contact}" target="_blank" rel="noopener noreferrer">${new URL(contact).host}</a>
                    </div>
                `;
                break;
            }
            case /@/.test(contact): {
                result += `
                    <div class="${containerClasses}">
                        <a href="mailto:${contact}" target="_blank" rel="noopener noreferrer">${contact}</a>
                    </div>
                `;
                break;
            }
            default:
                result += contact + (index < contacts.length - 1 ? '<br>' : '');
                break;
        }
    });

    return result;
}

function setProperIframeResize() {
    const body = $('body');

    body.on('mousedown', ({ target }) => {
        if (target.closest('.k-resize-handle') || target.closest('.k-window-titlebar')) {
            body.addClass('iframe-blocked');
            body.on('mouseup', cancelEffect);
        }
    });

    function cancelEffect() {
        body.removeClass('iframe-blocked');
        body.off('mouseup', cancelEffect);
    }
}

function floatingButtonsAutoTop() {
    const floatingButtonsTop = ($('.k-menu-horizontal').height() ?? 0) + 10;
    $('.floatingButtons').attr('style', `top: ${floatingButtonsTop}px`);
}

function changeLabel(labelname: string, text: string) {
    let label = $(`[for='${labelname}']`);

    if (!label.length) {
        label = $(`#${labelname}`);
    }

    if (text) {
        if (label.text() !== text.toString()) {
            label.text(text);
            label.trigger('change');
        }
    }
    else if (label.text()) {
        label.text('');
        label.trigger('change');
    }
}

function makeCall(number: string, self: Nullable<HTMLLinkElement> = null) {
    const url = `/Commutation/MakeCall?number=${number}`;

    if (!self || !$(self).attr('disabled')) {
        if (self) {
            $(self).attr('disabled', 'disabled');
        }

        window.ajaxGet(url, null, (response: { Data?: unknown; Message?: string }) => {
            if (self) {
                $(self).removeAttr('disabled');
            }

            if (!response.Data) {
                window.swal.fire('Внимание!', response.Message, 'warning');
            }
        });
    }
}

function updateCallLink(control: Iti, link: Element) {
    if (control.getValidationError()) {
        $(link).hide();
        return;
    }

    if (link instanceof HTMLAnchorElement) {
        link.href = control.getNumber(intlTelInput.utils?.numberFormat.E164);
        $(link).show();
    }
}

function btCreateFromObject_onClick() {
    if (window.pageYOffset > 0) {
        $(window).scrollTop(0);
        setTimeout(openCmCreateFromObject, 500);
    }
    else {
        openCmCreateFromObject();
    }
}

function openCmCreateFromObject() {
    $('#cmCreateFromObject').data('kendoContextMenu')?.open(0);
}

function getAllClientsByContact(url: string, contactId: string) {
    const data = { contactId };
    const clientsDiv = $('#clientsDiv');

    window.ajaxGet(url, data, (response: { Data?: string; Message?: string }) => {
        clientsDiv.html(response.Message ? 'По данному контакту не найдено контрагентов' : response.Data ?? '');

        if (clientsDiv.text() === '') {
            clientsDiv.text('Найден контрагент без имени');
        }
        return response;
    });
}

function placeholder(element: JQuery) {
    return element.clone().addClass('k-state-hover').css('opacity', 0.65);
}

function setIntlTelInput(control: JQuery<HTMLInputElement>, initialCountry: Iso2 = 'ru'): Iti | void {
    const inputNative = control.get(0);

    if (control.val() && !control.val()?.toString().startsWith('+')) {
        control.val('+' + control.val());
    }

    if (inputNative) {
        return window.getIntlTelInputInstance(inputNative, initialCountry);
    }
}

function getIntlTelInputInstance(input: HTMLInputElement, initialCountry: Iso2 = 'ru'): Iti {
    const existedInstance = itiInstances.get(input);

    if (existedInstance) {
        return existedInstance;
    }

    const instance = intlTelInput(input, {
        initialCountry,
        nationalMode: false,
        loadUtils: () => import('intl-tel-input/utils')
    });
    itiInstances.set(input, instance);

    return instance;
}

function destroyIntlTelInputInstance(input: HTMLInputElement) {
    itiInstances.get(input)?.destroy();
    itiInstances.delete(input);

    return null;
}

function btResetFilters_onClick() {
    location.href = location.origin + location.pathname;
}

function getYandexMapUrl(longitude: number, latitude: number) {
    return `https://maps.yandex.ru/?whatshere[point]=${longitude},${latitude}&whatshere[zoom]=17`;
}

/**
 * Обертка для вызова динамической функции из partial _AuditHistory,
 * поиск по названию: updateHistoryLabel@(suffix)()
 * */
function updateHistoryLabel(suffix: string): void {
    const fn = window[`updateHistoryLabel${suffix}`];

    if (typeof fn === 'function') {
        fn();
    }
}

function isMacOS() {
    return navigator.userAgent.indexOf('Mac OS') !== -1;
}

function normalizeHexColor(color: Nullable<string> | undefined) {
    if (!color) {
        return '';
    }
    return color.startsWith('#') ? color : `#${color}`;
}

function setCellBgColor(data: Record<string, string>) {
    const color = data[this.field];

    if (color) {
        const colorBg = normalizeHexColor(color);
        const colorText = window.invertColor(colorBg, true);
        return {
            style: `background-color: ${colorBg}; color: ${colorText};`,
            'data-disable-default-coloring-yes-no-unknown': 'true'
        };
    }

    return {};
}

/**
 * Устанавливает цвет фона ячейки Kendo Grid на основе значения TimeSpan:
 * отрицательные значения (содержащие '-') окрашиваются в красный (#ffc0cb),
 * положительные — в зелёный (#90ee90), нулевые/пустые — без цвета.
 * Используется как функция HtmlAttributes через bind({ field: 'FieldName' }).
 */
function setCellBgColorTimeSpan(data: Record<string, number | string>) {
    const value = data[this.field];
    const time = window.formatTime(value);

    if (window.isValid(time)) {
        return { style: `background-color: ${window.getTimeSpanColor(time)}` };
    }

    return {};
}

/**
 * Устанавливает цвет фона ячейки Kendo Grid:
 * при значении поля, равном 0 — colorIfZero, иначе — colorIfNotZero.
 * Используется как функция HtmlAttributes через bind({ field, colorIfZero, colorIfNotZero }).
 */
function setCellBgColorIntZero(data: Record<string, number | string> & { get?: (field: string) => Nullable<number | string | undefined> }) {
    const cellValue = typeof data.get === 'function' ? data.get(this.field) : data[this.field];

    if (cellValue === null || cellValue === undefined || cellValue === '') {
        return {};
    }

    const color = Number(cellValue) === 0 ? this.colorIfZero : this.colorIfNotZero;

    if (color) {
        return { style: `background-color: ${normalizeHexColor(color)}` };
    }

    return {};
}

function setCellBgColorWithClass(this: { field: string; cssClass: string }, data: Record<string, string>) {
    const color = data[this.field];
    const result: Record<string, string> = { class: this.cssClass };

    if (color) {
        const colorBg = normalizeHexColor(color);
        const colorText = window.invertColor(colorBg, true);
        result.style = `background-color: ${colorBg}; color: ${colorText};`;
        result['data-disable-default-coloring-yes-no-unknown'] = 'true';
    }

    return result;
}

/**
 * Устанавливает цвет фона ячейки Kendo Grid для дедлайна:
 * просроченные даты — красный (#ffc0cb), остальные — без подсветки (#f9fbff).
 * Используется как функция HtmlAttributes через bind({ field: 'FieldName' }).
 */
function setCellBgColorDeadline(data: Record<string, string | Date>) {
    const value = data[this.field];

    return { style: `background-color: ${window.getTicketDeadlineColor(value)}` };
}

/**
 * Устанавливает цвет текста ячейки Kendo Grid по статусу СМС.
 * Используется как функция HtmlAttributes через bind({ field: 'FieldName' }).
 */
function setCellSmsStatusColor(data: Record<string, string>) {
    const status = data[this.field];

    if (!status) {
        return {};
    }

    return { style: `color: ${window.getSmsStatusColor(status)}` };
}

/**
 * Устанавливает цвет фона ячейки Kendo Grid на основе значения с настраиваемыми порогами
 * Используется как функция HtmlAttributes через bind
 */
function setCellBgColorByValue(data: Record<string, number>) {
    const value = data[this.field];
    const threshold = this.threshold;
    const colorIfLess = this.colorIfLess;
    const colorIfEqual = this.colorIfEqual;
    const colorIfGreater = this.colorIfGreater;

    return { style: `background-color: ${window.getValueColor(value, threshold, colorIfLess, colorIfEqual, colorIfGreater)};` };
}

function setCellBgColorBoolean(
    this: {
        field: string;
        colorIfTrue?: string;
        colorIfFalse?: string;
    },
    data: Record<string, boolean>
): { style?: string } {
    const value = data[this.field];
    const color = value ? this.colorIfTrue : this.colorIfFalse;

    return { style: `background-color: ${color};` };
}

window.setProperIframeResize = setProperIframeResize;
window.lockElement = lockElement;
window.addPhoneIcons = addPhoneIcons;
window.audioBlock = audioBlock;
window.arrayContainsArray = arrayContainsArray;
window.btCreateFromObject_onClick = btCreateFromObject_onClick;
window.changeLabel = changeLabel;
window.btResetFilters_onClick = btResetFilters_onClick;
window.floatingButtonsAutoTop = floatingButtonsAutoTop;
window.getAllClientsByContact = getAllClientsByContact;
window.makeCall = makeCall;
window.getMaxOfArray = getMaxOfArray;
window.getYandexMapUrl = getYandexMapUrl;
window.NumberOrNull = NumberOrNull;
window.openCmCreateFromObject = openCmCreateFromObject;
window.placeholder = placeholder;
window.setIntlTelInput = setIntlTelInput;
window.getIntlTelInputInstance = getIntlTelInputInstance;
window.destroyIntlTelInputInstance = destroyIntlTelInputInstance;
window.PhoneOrContact = PhoneOrContact;
window.simpleDtoOrNull = simpleDtoOrNull;
window.updateCallLink = updateCallLink;
window.updateHistoryLabel = updateHistoryLabel;
window.isMacOS = isMacOS;
window.setCellBgColor = setCellBgColor;
window.setCellBgColorTimeSpan = setCellBgColorTimeSpan;
window.setCellBgColorIntZero = setCellBgColorIntZero;
window.setCellBgColorWithClass = setCellBgColorWithClass;
window.setCellBgColorDeadline = setCellBgColorDeadline;
window.setCellBgColorByValue = setCellBgColorByValue;
window.setCellBgColorBoolean = setCellBgColorBoolean;
window.setCellSmsStatusColor = setCellSmsStatusColor;
window.normalizeHexColor = normalizeHexColor;
